import { InvoiceTransactionHelper } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/hooks';
import { useExportHeader } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/hooks/useExport';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  ButtonGroup,
  ButtonV2,
  Form,
  InlineNotification,
  Loading,
  UploadDrawerLeadingFiles,
  UploadFiles,
  useToastAPI,
} from '@moneyforward/ap-frontend-components';
import { UnMatchHeader } from '@moneyforward/csv-excel-imp-exp-util';
import classNames from 'classnames';
import { FC, memo, Suspense, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { InvoiceTransaction, TransactionProperty } from '../../../type';
import {
  NotFoundRecord,
  useImport,
} from '../../InvoiceTransaction/hooks/useImport';
import styles from './CsvImportDrawer.module.scss';
import { Details } from './Details';

const cx = classNames.bind(styles);

export type ImportCsvForm = {
  file: UploadFiles;
};

export type Props = {
  onImport: (items: InvoiceTransaction[]) => void;
  invoiceTransactionDetails: boolean;
  taxInclude: boolean;
  hasWithholdingIncomeTax: boolean;
  canDisplayDrExcise: boolean;
  isAdminOrApproval: boolean;
  updateExciseValue: InvoiceTransactionHelper['updateExciseValue'];
  transactionProperties?: TransactionProperty[];
};

export const InnerCsvImportDrawer: FC<Props> = memo(
  ({
    onImport,
    invoiceTransactionDetails,
    taxInclude,
    hasWithholdingIncomeTax,
    canDisplayDrExcise,
    isAdminOrApproval,
    updateExciseValue,
    transactionProperties = [],
  }) => {
    const { t } = useTranslation();
    const [errorMsg, setErrorMsg] = useState<string>('');
    const [open, setOpen] = useState(false);
    const onOpen = useCallback(() => {
      setOpen(true);
    }, []);

    const {
      onExportsSjisCsvOnlyHeader: exportSjisCsv,
      onExportsUtf8CsvOnlyHeader: exportUtf8Csv,
      onExportsXlsxWithOnlyHeader: exportXlsx,
    } = useExportHeader({
      taxInclude,
      invoiceTransactionDetails,
      isAdminOrApproval,
      hasWithholdingIncomeTax,
      canDisplayDrExcise,
      transactionProperties,
    });
    const methods = useForm<ImportCsvForm>({
      defaultValues: {
        file: [],
      },
    });

    const { reset, getValues } = methods;

    const uploader = useMemo(
      () => ({
        append: false,
        description: t('invoice_reports.csv_import_drawer.description'),
        accept: '.csv, .xlsx',
        name: 'file' as const,
        control: methods.control,
      }),
      [t, methods]
    );

    const notify = useToastAPI();
    const { importInvoiceTransaction } = useImport(
      taxInclude,
      invoiceTransactionDetails,
      isAdminOrApproval,
      hasWithholdingIncomeTax,
      canDisplayDrExcise,
      transactionProperties,
      updateExciseValue
    );
    const onReplace = useCallback(async () => {
      const fileWatch = getValues('file');
      const importData = await importInvoiceTransaction({
        file: fileWatch[0],
      });
      onImport(importData);
    }, [getValues, importInvoiceTransaction, onImport]);

    const onClose = useCallback(() => {
      setOpen(false);
      reset();
      setErrorMsg('');
    }, [reset]);
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const onSubmit = useCallback(async () => {
      try {
        setIsSubmitLoading(true);
        try {
          await onReplace();
          notify.success({
            title: t('invoice_reports.csv_import_drawer.import_success'),
            duration: 5,
          });
          reset();
        } finally {
          setIsSubmitLoading(false);
        }
      } catch (error: unknown) {
        if (error instanceof NotFoundRecord) {
          notify.error({
            title: t('invoice_reports.csv_import_drawer.import_error.notfound'),
            duration: 5,
          });
        } else if (error instanceof UnMatchHeader) {
          notify.error({
            title: t(
              'invoice_reports.csv_import_drawer.import_error.un_match_header'
            ),
            duration: 5,
          });
        } else {
          notify.error({
            title: t('system_error'),
            duration: 5,
          });
        }
      } finally {
        setOpen(false);
      }
    }, [reset, notify, onReplace, t]);
    const containerRef = useGlobalContainerRef();
    return (
      <div>
        <Loading
          getContainer={containerRef}
          open={isSubmitLoading}
          isDialogMode
          size='xlarge'
          color='white'
        />
        <ButtonV2
          onClick={onOpen}
          isSecondary
          isTertiary
          testId='import-button'
        >
          {t('import_label')}
        </ButtonV2>
        <Form<ImportCsvForm> formMethod={methods}>
          <div className={cx(styles['uploader-container'])}>
            <UploadDrawerLeadingFiles<ImportCsvForm>
              buttons={
                <div>
                  <ButtonGroup>
                    <ButtonV2
                      color='primary'
                      isSecondary
                      onClick={onClose}
                      type='button'
                    >
                      {t('cancel')}
                    </ButtonV2>
                    <ButtonV2
                      color='primary'
                      type='button'
                      isDisabled={methods.getValues('file').length === 0}
                      testId='submit-button'
                      onClick={onSubmit}
                    >
                      {t('import_label')}
                    </ButtonV2>
                  </ButtonGroup>
                </div>
              }
              title={t('invoice_reports.csv_import_drawer.title')}
              open={open}
              size='slim'
              onClose={onClose}
              control={methods.control}
              name='file'
              arrayName='file'
              uploader={uploader}
              uploadFileLimitTotal={1}
              header={
                <>
                  <InlineNotification
                    message={errorMsg}
                    type='error'
                    visible={errorMsg ? true : false}
                    onClick={() => setErrorMsg('')}
                  />
                </>
              }
              details={
                <Details
                  exportSjisCsv={exportSjisCsv}
                  exportUtf8Csv={exportUtf8Csv}
                  exportXlsx={exportXlsx}
                  isApproverRoleFromPath={isAdminOrApproval}
                />
              }
            />
          </div>
        </Form>
      </div>
    );
  }
);
InnerCsvImportDrawer.displayName = 'InnerCsvImportDrawer';

export const CsvImportDrawer: FC<Props> = memo((props) => {
  return (
    <Suspense fallback={<ButtonV2 isLoading />}>
      <InnerCsvImportDrawer {...props} />
    </Suspense>
  );
});
CsvImportDrawer.displayName = 'CsvImportDrawer';
